import React, { useEffect, useState } from 'react';

import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Grid,
  IconButton,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import TableChartIcon from '@mui/icons-material/TableChart';
import ListTwoToneIcon from '@mui/icons-material/ListTwoTone';

import { useSelector } from 'react-redux';
import { COLOR_GREY_600, COLOR_PRIMARY_2 } from 'src/colors';
import TabBar from '../Components/Tabs/TabBar';
import { LIGHT_GREY } from 'src/const';
import { getRedirectionLink, getVisitedMarketsFromLocalStorage } from 'src/utils/functions';

const MarketCard = ({ district, country, city, isSubscribed, isDemo }) => {
  const handleClick = () => {
    const redirectTo = getRedirectionLink(country.replaceAll(' ', '-'), city, district);
    window.history.pushState({}, '', redirectTo);
    window.location.reload();
  };

  return (
    <Card
      sx={{
        position: 'relative',
        display: 'flex',
        minWidth: '300px',
        maxWidth: '400px',
        width: '100%',
        cursor: 'pointer',
      }}
      onClick={handleClick}
    >
      <CardMedia
        component="img"
        sx={{ width: '35%' }}
        image="https://airbtics-wordpress-market-assets.s3.us-east-2.amazonaws.com/map-image-no-label-demo.png"
      />
      {(isSubscribed || isDemo) && (
        <Chip
          label={isDemo ? 'Demo' : 'Subscribed'}
          sx={{
            position: 'absolute',
            top: '4px',
            left: '4px',
            background: '#fff',
            border: '1px solid ' + COLOR_PRIMARY_2,
            color: COLOR_PRIMARY_2,
            fontWeight: 'bold',
            textTransform: 'capitalize',
          }}
        />
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <CardContent>
          <Typography
            variant="h6"
            component="div"
            sx={{ fontWeight: 'bold', textTransform: 'capitalize' }}
          >
            {district.slice(0, 12)} {district.length > 12 && '...'}
          </Typography>
        </CardContent>
        <CardContent sx={{ paddingBottom: '10 !important' }}>
          <Typography
            variant="subtitle1"
            sx={{ alignSelf: 'flex-end', fontSize: '14px', textTransform: 'capitalize' }}
          >
            {country != '0' && country.slice(0, 15)}
            {country != '0' && country.length > 15 && '...'}
            {country == '0' && <>&nbsp;</>}
          </Typography>
        </CardContent>
      </Box>
    </Card>
  );
};

const CustomSwitch = ({ view, setView }) => {
  const theme = useTheme();

  return (
    <Paper
      sx={{
        padding: 1,
        borderRadius: 20,
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[3],
      }}
    >
      <Grid
        container
        direction="row"
        justifyContent="center"
        alignItems="center"
        sx={{
          background: LIGHT_GREY,
          borderRadius: 100,
        }}
      >
        <IconButton
          sx={{
            fontSize: 10,
            backgroundColor: view == 'list' ? COLOR_PRIMARY_2 : LIGHT_GREY,
          }}
          onClick={() => setView('list')}
        >
          <ListTwoToneIcon
            sx={{
              color: view == 'list' ? 'white' : COLOR_GREY_600,
            }}
          />
        </IconButton>

        <IconButton
          sx={{
            fontSize: 10,
            backgroundColor: view == 'table' ? COLOR_PRIMARY_2 : LIGHT_GREY,
          }}
          onClick={() => setView('table')}
        >
          <TableChartIcon
            sx={{
              color: view == 'table' ? 'white' : COLOR_GREY_600,
            }}
          />
        </IconButton>
      </Grid>
    </Paper>
  );
};

const OverviewLandingPage = ({
  SearchBox,
  handleMarketSidebarClick,
  dummyMarket,
}: {
  SearchBox: (isOnboardingOn: boolean) => JSX.Element;
  handleMarketSidebarClick: (event: string) => Promise<void>;
  dummyMarket: any;
}) => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down(900));
  const user = useSelector(state => (state as any).dashboard.user);

  const [recentlySearchedMarkets, setRecentlySearchedMarkets] = useState<
    { district: string; country: string; city: string; isSubscribed: boolean; isDemo: boolean }[]
  >([]);

  useEffect(() => {
    const subscribedMarkets =
      user.regions &&
      Array.isArray(user.regions) &&
      [...user.regions].reverse().map(market => ({
        district: market.region,
        country: market.country ? market.country?.toLowerCase() : '0',
        city: market.city ? market.city?.toLowerCase() : '0',
        isSubscribed: true,
        isDemo: market.region?.toLowerCase() === dummyMarket.district?.toLowerCase(),
      }));
    const recentlyVisitedMarkets = getVisitedMarketsFromLocalStorage();

    const marketMap = new Map();

    recentlyVisitedMarkets &&
      Array.isArray(recentlyVisitedMarkets) &&
      recentlyVisitedMarkets.reverse().forEach(market => {
        marketMap.set(market.district?.toLowerCase(), {
          ...market,
          country: market.country ? market.country?.toLowerCase() : '0',
          city: market.city ? market.city?.toLowerCase() : '0',
          isSubscribed: false,
          isDemo: market.district?.toLowerCase() === dummyMarket.district?.toLowerCase(),
        });
      });

    subscribedMarkets.forEach(market => {
      marketMap.set(market.district?.toLowerCase(), market);
    });

    const uniqueMarkets = Array.from(marketMap.values());
    console.log(uniqueMarkets);
    setRecentlySearchedMarkets(uniqueMarkets);
  }, [user]);

  const overviewLandingTabs = [
    {
      label: 'Recently Searched',
      value: 'recent',
      icon: null,
      onclick: () => {
        setTab('recent');
      },
    },
    {
      label: 'Saved',
      value: 'saved',
      icon: null,
      onclick: () => {
        setTab('saved');
      },
    },
  ];

  const [tab, setTab] = useState<'recent' | 'saved'>('recent');
  const [view, setView] = useState<'list' | 'table'>('list');

  useEffect(() => {
    if (view == 'table') {
      handleMarketSidebarClick('advance');
    }
  }, [view]);

  return (
    <Grid
      container
      item
      sx={{
        paddingBottom: matches ? '270px' : '70px',
        background: '#FFF',
        width: '100%',
      }}
      xs={12}
    >
      <Grid
        container
        item
        xs={12}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '20px',
          height: '50%',
        }}
      >
        <Grid container item justifyContent="center" xs={12}>
          <Grid
            item
            container
            sx={{
              padding: '5px',
              height: '90px',
              maxWidth: '1600px',
              borderRadius: '12px',
              background: '#fff',
              mt: 2,
            }}
            lg={8}
            md={8}
            xs={12}
          >
            {SearchBox(true)}
          </Grid>
        </Grid>
      </Grid>

      <Grid
        container
        item
        xs={12}
        sx={{
          width: '100%',
          backgroundColor: '#E7F2F4',
          minHeight: '100px',
          borderTopLeftRadius: '40px',
        }}
      >
        <Grid
          container
          item
          xs
          sx={{
            background: '#fff',
            marginTop: '20px',
            borderTopLeftRadius: '40px',
            padding: '20px',
          }}
        >
          <Grid item xs={12}>
            <TabBar
              tabList={overviewLandingTabs}
              currentTab={tab}
              color={COLOR_PRIMARY_2}
              renderLabelCondition={() => true}
              actionButton={[<CustomSwitch view={view} setView={setView} />]}
              showActionButton={true}
              transparentBackground={true}
            />
          </Grid>

          {tab == 'recent' && (
            <Grid container item spacing={4} sx={{ marginTop: '20px' }}>
              {recentlySearchedMarkets.map((market, index) => (
                <Grid item xs={12} sm={6} lg={4} xl={3} key={index}>
                  <MarketCard
                    district={market.district}
                    country={market.country}
                    isSubscribed={market.isSubscribed}
                    city={market.city}
                    isDemo={market.isDemo}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default OverviewLandingPage;
